/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { ArrowDown, ArrowUp, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';
import { useOverviewDisplay } from '@/context/OverviewDisplayContext';

/* Import configuration */
import { OVERVIEW_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';
import type { OverviewAnomalyConfig } from '@/config/kpi/overviewAnomalyConfig';

/* Import components */
import Api2LastValueData from '@/components/api/Api2LastValueData';
import AppLoader from '@/components/feedback/AppLoader';
import OverviewAnomalyCard from '@/components/tabs/overview/metrics/cards/OverviewAnomalyCard';
import OverviewCustomerCard from '@/components/tabs/overview/metrics/cards/OverviewCustomerCard';
import OverviewSensorCard, { type OverviewSensorCardData } from '@/components/tabs/overview/metrics/cards/OverviewSensorCard';
import OverviewStatusCard from '@/components/tabs/overview/metrics/cards/OverviewStatusCard';
import OverviewSystemCard from '@/components/tabs/overview/metrics/cards/OverviewSystemCard';

/* Import libraries */
import type { Api2ValueDataItem } from '@/lib/overview/api2ValueData';
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { NamingConventions } from '@/lib/formatting/namingConventions';
import {
    formatOverviewSortLabel,
    getAvailableOverviewMetadataSortKeys,
    OVERVIEW_METADATA_SORT_KEYS,
    OVERVIEW_STATUS_CATEGORIES,
    getOverviewStatusCategory,
    sortOverviewCardItems,
    type OverviewMetadataSortKey,
    type OverviewSortDirection,
    type OverviewStatusCategory,
} from '@/lib/overview/overviewCards';
import { createOverviewDataset, type OverviewDataset } from '@/lib/overview/overviewDataset';
import {
    aggregateOverviewCardsByAnomaly,
    aggregateOverviewCardsByCustomer,
    aggregateOverviewCardsByStatus,
    aggregateOverviewCardsBySystem,
    sortOverviewAggregatedCardsByKpi,
    STATUS_SENSOR_COUNT_SORT_KEYS,
    SYSTEM_SENSOR_COUNT_SORT_KEY,
    sortOverviewSystemCardsByKpi,
} from '@/lib/overview/overviewSystemAggregation';
import { ReverseDeviceSensor } from '@/lib/sensors/catalog/reverseDeviceSensor';
import { sortApi2TimeSeriesDataItems } from '@/lib/overview/sortApi2TimeSeriesDataItems';
import {logDebug} from "@/lib/app/debugLog";

type OverviewGroupBy = 'none' | 'anomaly' | 'customer' | 'status' | 'system';

const SYSTEM_NAME_SORT_KEY = 'name';

export interface AnomalyFilterOption {
    anomalies: readonly string[];
    label: string;
    showWhenEmpty?: boolean;
    value: string;
}

interface AnomalyMetricsOverviewProps {
    anomalyFilterOptions?: readonly AnomalyFilterOption[];
    anomalyConfigs: readonly OverviewAnomalyConfig[];
    description: string;
    fixedCustomer?: string;
    fixedStatus?: OverviewStatusCategory;
    fixedSystem?: string;
    gridClassName?: string;
    groupBy?: OverviewGroupBy;
    heading: string;
    idPrefix: string;
    includeDisabledSensors?: boolean;
    onSelectedAnomalyChange?: (anomaly: string) => void;
    selectedAnomaly?: string;
    showSystemSortGroups?: boolean;
}

function getUniqueSortedValues(values: string[]): string[] {
    return Array.from(new Set(values.filter(Boolean))).sort();
}

function getDefaultAnomalyFilterLabel(anomaly: string): string {
    if (anomaly === 'pv') {
        return 'PV Prediction';
    }

    if (anomaly === 'bl') {
        return 'Base Load';
    }

    if (anomaly === 'bl_night') {
        return 'Base Load Night';
    }

    if (anomaly === 'heating_energy') {
        return 'Heating Energy';
    }

    return new NamingConventions(anomaly).getTitle();
}

function getErrorMessage(errorMessage: string | string[]): string {
    return Array.isArray(errorMessage)
        ? errorMessage.find(Boolean) ?? ''
        : errorMessage;
}

function isMetadataSortKey(value: string): value is OverviewMetadataSortKey {
    return OVERVIEW_METADATA_SORT_KEYS.includes(value as OverviewMetadataSortKey);
}

function matchesOverviewStatus(
    status: number,
    selectedStatus: OverviewStatusCategory | '',
): boolean {
    return selectedStatus === '' || getOverviewStatusCategory(status) === selectedStatus;
}

const overviewSensorCardsCache = new WeakMap<
    Api2ValueDataItem[],
    WeakMap<OverviewDataset, OverviewSensorCardData[]>
>();

function createOverviewSensorCards(
    items: Api2ValueDataItem[],
    overviewDataset: OverviewDataset,
    deviceSensorCatalog: DeviceSensorCatalog,
): OverviewSensorCardData[] {
    return items.map((item): OverviewSensorCardData | null => {
        const resolvedUrn = item.urn.getFirst();

        if (resolvedUrn === null) {
            return null;
        }

        const anomalyConfig = overviewDataset.definitionsByUrn.get(resolvedUrn);

        if (!anomalyConfig) {
            return null;
        }

        const reverseDeviceSensor = new ReverseDeviceSensor(
            resolvedUrn,
            deviceSensorCatalog,
        );
        const deviceName = reverseDeviceSensor.getVirtualDeviceName();

        if (deviceName === null) {
            return null;
        }

        const metadata = deviceSensorCatalog.getSensorMetadata(
            deviceName,
            anomalyConfig.group,
        );

        if (metadata === null) {
            return null;
        }

        return {
            address: metadata.address,
            anomaly: metadata.anomaly ?? '',
            anomalyConfig,
            customer: metadata.customer ?? '',
            deviceName,
            deviceSensorPart: new DeviceSensor(deviceName, deviceSensorCatalog).getDeviceSensorPart(),
            errorMessage: getErrorMessage(item.errorMessage),
            isEnabled: deviceSensorCatalog.isGroupEnabled(deviceName, anomalyConfig.group),
            item,
            metadata,
            name: metadata.name ?? metadata.system ?? deviceName,
            sensor: deviceName,
            status: Number(item.rawValue.get('status') ?? 0),
            system: metadata.system ?? '',
        };
    }).filter((card): card is OverviewSensorCardData => card !== null);
}

function getOverviewSensorCards(
    items: Api2ValueDataItem[],
    overviewDataset: OverviewDataset,
    deviceSensorCatalog: DeviceSensorCatalog,
): OverviewSensorCardData[] {
    const cardsByDataset = overviewSensorCardsCache.get(items);
    const cachedCards = cardsByDataset?.get(overviewDataset);

    if (cachedCards) {
        return cachedCards;
    }

    const cards = createOverviewSensorCards(items, overviewDataset, deviceSensorCatalog);
    const nextCardsByDataset = cardsByDataset ?? new WeakMap<OverviewDataset, OverviewSensorCardData[]>();

    nextCardsByDataset.set(overviewDataset, cards);
    overviewSensorCardsCache.set(items, nextCardsByDataset);

    return cards;
}

function AnomalyMetricsOverview({
    anomalyFilterOptions: configuredAnomalyFilterOptions,
    anomalyConfigs,
    description,
    fixedCustomer,
    fixedStatus,
    fixedSystem,
    gridClassName = 'xl:grid-cols-4',
    groupBy = 'none',
    heading,
    idPrefix,
    includeDisabledSensors = false,
    onSelectedAnomalyChange,
    selectedAnomaly: selectedAnomalyProp,
    showSystemSortGroups = false,
}: AnomalyMetricsOverviewProps) {
    const { t } = useI18n();
    const { showKpis } = useOverviewDisplay();
    const [selectedSortSensorType, setSelectedSortSensorType] = useState('status');
    const [selectedSortDirection, setSelectedSortDirection] = useState<OverviewSortDirection>('desc');
    const [localSelectedAnomaly, setLocalSelectedAnomaly] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<OverviewStatusCategory | ''>('');
    const [selectedSystem, setSelectedSystem] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const deviceSensorCatalog = useMemo(() => {
        return new DeviceSensorCatalog(OVERVIEW_DEVICE_SENSOR_CONFIG);
    }, []);
    const overviewDataset = useMemo(() => {
        return createOverviewDataset(deviceSensorCatalog, anomalyConfigs, {
            enabledOnly: !includeDisabledSensors,
        });
    }, [anomalyConfigs, deviceSensorCatalog, includeDisabledSensors]);
    const isSimplifiedSystemOverview = groupBy === 'system' && !showKpis;
    const isAnomalyFilterHidden = isSimplifiedSystemOverview;
    const isCustomerFilterFixed = fixedCustomer !== undefined || fixedSystem !== undefined;
    const isStatusFilterHidden = isSimplifiedSystemOverview || fixedStatus !== undefined;
    const isSystemFilterHidden = isSimplifiedSystemOverview || fixedSystem !== undefined;
    const selectedAnomaly = selectedAnomalyProp ?? localSelectedAnomaly;

    function setSelectedAnomaly(anomaly: string) {
        if (selectedAnomalyProp === undefined) {
            setLocalSelectedAnomaly(anomaly);
        }

        onSelectedAnomalyChange?.(anomaly);
    }

    logDebug('info', {
        type: 'SÜ BK',
        source: 'src/components/tabs/overview/metrics/AnomalyMetricsOverview.tsx',
        function: 'AnomalyMetricsOverview',
        message: 'Aus OVERVIEW_ANOMALY_CONFIGS wird das API-Dataset erzeugt: `overviewDataset`',
        content: overviewDataset,
    });

    return (
        <div className="flex flex-col gap-6" aria-label={t(heading)}>
            <section className="flex flex-col gap-2">
                <h2>{t(heading)}</h2>
                <p>{t(description)}</p>
            </section>

            <section className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${gridClassName}`}>
                <Api2LastValueData
                    date="NOW"
                    urn={overviewDataset.urn}
                    calc={overviewDataset.calc}
                    conversion={overviewDataset.conversion}
                    fractionDigits={overviewDataset.fractionDigits}
                    title={overviewDataset.title}
                    unit={overviewDataset.unit}
                >
                    {({ items, isLoading, loadingLabel }) => {
                        if (isLoading) {
                            return (
                                <div className="col-span-full flex h-full w-full items-center justify-center">
                                    <AppLoader label={loadingLabel || t('Loading API request...')} />
                                </div>
                            );
                        }

                        const cards = getOverviewSensorCards(
                            items,
                            overviewDataset,
                            deviceSensorCatalog,
                        );

                        const availableAnomalies = getUniqueSortedValues(cards.map((card) => card.anomaly));
                        const anomalyFilterOptions = configuredAnomalyFilterOptions
                            ? configuredAnomalyFilterOptions.filter(({ anomalies, showWhenEmpty }) => (
                                showWhenEmpty || anomalies.some((anomaly) => availableAnomalies.includes(anomaly))
                            ))
                            : availableAnomalies.map((anomaly) => ({
                                anomalies: [anomaly],
                                label: getDefaultAnomalyFilterLabel(anomaly),
                                value: anomaly,
                            }));
                        const systemOptions = getUniqueSortedValues(cards.map((card) => card.system));
                        const customerOptions = getUniqueSortedValues(cards.map((card) => card.customer));
                        const availableMetadataSortKeys = getAvailableOverviewMetadataSortKeys(
                            customerOptions.length,
                        );
                        const isCustomerFilterHidden = isCustomerFilterFixed || customerOptions.length <= 1;
                        const effectiveSelectedAnomaly = !isSimplifiedSystemOverview && anomalyFilterOptions.some(({ value }) => value === selectedAnomaly)
                            ? selectedAnomaly
                            : '';
                        const selectedAnomalyValues = anomalyFilterOptions.find(({ value }) => (
                            value === effectiveSelectedAnomaly
                        ))?.anomalies ?? [];
                        const effectiveSelectedSystem = isSimplifiedSystemOverview
                            ? ''
                            : fixedSystem ?? (
                            systemOptions.includes(selectedSystem) ? selectedSystem : ''
                            );
                        const effectiveSelectedCustomer = fixedCustomer ?? (
                            customerOptions.includes(selectedCustomer) ? selectedCustomer : ''
                        );
                        const effectiveSelectedStatus = isSimplifiedSystemOverview
                            ? ''
                            : fixedStatus ?? (
                            selectedStatus !== '' && OVERVIEW_STATUS_CATEGORIES.includes(selectedStatus)
                                ? selectedStatus
                                : ''
                            );
                        const filteredCards = cards
                            .filter((card) => !effectiveSelectedAnomaly || selectedAnomalyValues.includes(card.anomaly))
                            .filter((card) => !effectiveSelectedSystem || card.system === effectiveSelectedSystem)
                            .filter((card) => !effectiveSelectedCustomer || card.customer === effectiveSelectedCustomer);
                        const activeAnomalyConfigs = anomalyConfigs.length === 1
                            ? anomalyConfigs
                            : Array.from(new Set(
                                filteredCards.map((card) => card.anomalyConfig),
                            ));
                        const dynamicSortKeys = anomalyConfigs.length > 1 && !effectiveSelectedAnomaly
                            ? ['status']
                            : Array.from(new Set([
                                ...activeAnomalyConfigs.flatMap((config) => (
                                    config.kpis.map((kpi) => kpi.sensorType)
                                )),
                                'status',
                            ]));
                        const isGroupedOverview = groupBy !== 'none';
                        const availableSortSensorTypes = isSimplifiedSystemOverview
                            ? ['status', SYSTEM_NAME_SORT_KEY]
                            : isGroupedOverview
                            ? [
                                ...dynamicSortKeys,
                                SYSTEM_SENSOR_COUNT_SORT_KEY,
                                ...STATUS_SENSOR_COUNT_SORT_KEYS,
                            ]
                            : dynamicSortKeys;
                        const effectiveSelectedSortSensorType = isSimplifiedSystemOverview
                            ? selectedSortSensorType === SYSTEM_NAME_SORT_KEY
                                ? SYSTEM_NAME_SORT_KEY
                                : 'status'
                            : (
                            selectedSortSensorType === '' ||
                            availableMetadataSortKeys.includes(selectedSortSensorType as OverviewMetadataSortKey) ||
                            availableSortSensorTypes.includes(selectedSortSensorType)
                        )
                            ? selectedSortSensorType
                            : 'status';
                        const effectiveSelectedSortDirection = isSimplifiedSystemOverview
                            ? effectiveSelectedSortSensorType === SYSTEM_NAME_SORT_KEY
                                ? selectedSortDirection
                                : 'desc'
                            : selectedSortDirection;
                        let sortedCards = filteredCards.filter((card) => (
                            matchesOverviewStatus(card.status, effectiveSelectedStatus)
                        ));
                        let sortedAnomalyCards = groupBy === 'anomaly'
                            ? aggregateOverviewCardsByAnomaly(filteredCards).filter((card) => (
                                matchesOverviewStatus(card.status, effectiveSelectedStatus)
                            ))
                            : [];
                        let sortedSystemCards = groupBy === 'system'
                            ? aggregateOverviewCardsBySystem(filteredCards).filter((card) => (
                                matchesOverviewStatus(card.status, effectiveSelectedStatus)
                            ))
                            : [];
                        let sortedStatusCards = groupBy === 'status'
                            ? aggregateOverviewCardsByStatus(filteredCards).filter((card) => (
                                matchesOverviewStatus(card.status, effectiveSelectedStatus)
                            ))
                            : [];
                        let sortedCustomerCards = groupBy === 'customer'
                            ? aggregateOverviewCardsByCustomer(filteredCards).filter((card) => (
                                matchesOverviewStatus(card.status, effectiveSelectedStatus)
                            ))
                            : [];

                        if (groupBy === 'anomaly') {
                            if (isMetadataSortKey(effectiveSelectedSortSensorType)) {
                                sortedAnomalyCards = sortOverviewCardItems(sortedAnomalyCards, {
                                    by: effectiveSelectedSortSensorType,
                                    direction: effectiveSelectedSortDirection,
                                });
                            } else if (effectiveSelectedSortSensorType) {
                                sortedAnomalyCards = sortOverviewAggregatedCardsByKpi(
                                    sortedAnomalyCards,
                                    effectiveSelectedSortSensorType,
                                    effectiveSelectedSortDirection,
                                );
                            }
                        } else if (groupBy === 'system') {
                            if (effectiveSelectedSortSensorType === SYSTEM_NAME_SORT_KEY) {
                                const directionFactor = effectiveSelectedSortDirection === 'desc' ? -1 : 1;

                                sortedSystemCards = [...sortedSystemCards].sort((leftCard, rightCard) => (
                                    leftCard.name.localeCompare(rightCard.name) * directionFactor
                                ));
                            } else if (isMetadataSortKey(effectiveSelectedSortSensorType)) {
                                sortedSystemCards = sortOverviewCardItems(sortedSystemCards, {
                                    by: effectiveSelectedSortSensorType,
                                    direction: effectiveSelectedSortDirection,
                                });
                            } else if (effectiveSelectedSortSensorType) {
                                sortedSystemCards = sortOverviewSystemCardsByKpi(
                                    sortedSystemCards,
                                    effectiveSelectedSortSensorType,
                                    effectiveSelectedSortDirection,
                                );
                            }
                        } else if (groupBy === 'status') {
                            if (isMetadataSortKey(effectiveSelectedSortSensorType)) {
                                sortedStatusCards = sortOverviewCardItems(sortedStatusCards, {
                                    by: effectiveSelectedSortSensorType,
                                    direction: effectiveSelectedSortDirection,
                                });
                            } else if (effectiveSelectedSortSensorType) {
                                sortedStatusCards = sortOverviewAggregatedCardsByKpi(
                                    sortedStatusCards,
                                    effectiveSelectedSortSensorType,
                                    effectiveSelectedSortDirection,
                                );
                            }
                        } else if (groupBy === 'customer') {
                            if (isMetadataSortKey(effectiveSelectedSortSensorType)) {
                                sortedCustomerCards = sortOverviewCardItems(sortedCustomerCards, {
                                    by: effectiveSelectedSortSensorType,
                                    direction: effectiveSelectedSortDirection,
                                });
                            } else if (effectiveSelectedSortSensorType) {
                                sortedCustomerCards = sortOverviewAggregatedCardsByKpi(
                                    sortedCustomerCards,
                                    effectiveSelectedSortSensorType,
                                    effectiveSelectedSortDirection,
                                );
                            }
                        } else if (isMetadataSortKey(effectiveSelectedSortSensorType)) {
                            sortedCards = sortOverviewCardItems(sortedCards, {
                                by: effectiveSelectedSortSensorType,
                                direction: effectiveSelectedSortDirection,
                            });
                        } else if (effectiveSelectedSortSensorType) {
                            const cardsByItemKey = new Map(
                                sortedCards.map((card) => [card.item.key, card]),
                            );

                            sortedCards = sortApi2TimeSeriesDataItems(
                                sortedCards.map((card) => card.item),
                                {
                                    by: effectiveSelectedSortSensorType,
                                    direction: effectiveSelectedSortDirection,
                                    value: 'rawValue',
                                },
                            ).flatMap((item) => {
                                const card = cardsByItemKey.get(item.key);

                                return card ? [card] : [];
                            });
                        }

                        if (includeDisabledSensors && groupBy === 'none') {
                            sortedCards = [...sortedCards].sort((leftCard, rightCard) => (
                                Number(rightCard.isEnabled) - Number(leftCard.isEnabled)
                            ));
                        }

                        const displayedCardCount = groupBy === 'anomaly'
                            ? sortedAnomalyCards.length
                            : groupBy === 'system'
                                ? sortedSystemCards.length
                                : groupBy === 'status'
                                    ? sortedStatusCards.length
                                    : groupBy === 'customer'
                                        ? sortedCustomerCards.length
                                : sortedCards.length;
                        const displayedCardTypeLabel = groupBy === 'anomaly'
                            ? t('Anomalies')
                            : groupBy === 'system'
                                ? t('Systems')
                                : groupBy === 'status'
                                    ? t('Statuses')
                                    : groupBy === 'customer'
                                        ? t('Customers')
                                : t('Sensors');
                        const systemStatusGroups = groupBy === 'system' &&
                            showSystemSortGroups &&
                            effectiveSelectedSortSensorType === 'status'
                            ? OVERVIEW_STATUS_CATEGORIES.map((statusCategory) => ({
                                cards: sortedSystemCards.filter((card) => (
                                    getOverviewStatusCategory(card.status) === statusCategory
                                )),
                                statusCategory,
                            })).filter((statusGroup) => statusGroup.cards.length > 0)
                            : [];
                        const shouldShowSystemStatusGroups = systemStatusGroups.length > 0;
                        const systemNameGroups = groupBy === 'system' &&
                            showSystemSortGroups &&
                            effectiveSelectedSortSensorType === SYSTEM_NAME_SORT_KEY
                            ? Array.from(sortedSystemCards.reduce<Map<string, typeof sortedSystemCards>>(
                                (groups, card) => {
                                    const initial = card.name.trim().charAt(0).toLocaleUpperCase() || '#';
                                    const cardsForInitial = groups.get(initial) ?? [];

                                    cardsForInitial.push(card);
                                    groups.set(initial, cardsForInitial);

                                    return groups;
                                },
                                new Map(),
                            ).entries()).map(([initial, cards]) => ({ cards, initial }))
                            : [];
                        const shouldShowSystemNameGroups = systemNameGroups.length > 0;
                        const shouldShowResetSelectionButton = !isSimplifiedSystemOverview;

                        const shouldShowToolbar = groupBy === 'anomaly'
                            ? aggregateOverviewCardsByAnomaly(cards).length > 1
                            : groupBy === 'system'
                                ? aggregateOverviewCardsBySystem(cards).length > 1
                                : groupBy === 'status'
                                    ? aggregateOverviewCardsByStatus(cards).length > 1
                                    : groupBy === 'customer'
                                        ? aggregateOverviewCardsByCustomer(cards).length > 1
                                : cards.length > 1;

                        return (
                            <>
                                {shouldShowToolbar ? (
                                    <div className="col-span-full">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div className="inline-flex flex-wrap items-center gap-3">
                                                <label
                                                    className="text-sm font-semibold"
                                                    htmlFor={`${idPrefix}-sort-select`}
                                                >
                                                    {t('Sort')}
                                                </label>
                                                <select
                                                    id={`${idPrefix}-sort-select`}
                                                    className="select select-bordered w-auto"
                                                    value={effectiveSelectedSortSensorType}
                                                    onChange={(event) => {
                                                        const selectedSort = event.target.value;

                                                        setSelectedSortSensorType(selectedSort);

                                                        if (isSimplifiedSystemOverview) {
                                                            setSelectedSortDirection(
                                                                selectedSort === SYSTEM_NAME_SORT_KEY ? 'asc' : 'desc',
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {!isSimplifiedSystemOverview ? (
                                                        <>
                                                            <option value="">
                                                                {t('No sorting')}
                                                            </option>
                                                            <option disabled>──────────</option>
                                                        </>
                                                    ) : null}
                                                    {availableSortSensorTypes.map((sensorType) => (
                                                        <option key={sensorType} value={sensorType}>
                                                            {sensorType === SYSTEM_NAME_SORT_KEY
                                                                ? t('Name')
                                                                : sensorType === SYSTEM_SENSOR_COUNT_SORT_KEY
                                                                ? t('Sensor count')
                                                                : sensorType === 'dangerSensorCount'
                                                                    ? t('Danger sensor count')
                                                                    : sensorType === 'warningSensorCount'
                                                                        ? t('Warning sensor count')
                                                                        : sensorType === 'successSensorCount'
                                                                            ? t('Success sensor count')
                                                                            : new NamingConventions(sensorType).getTitle()}
                                                        </option>
                                                    ))}
                                                    {!isSimplifiedSystemOverview ? (
                                                        <>
                                                            <option disabled>──────────</option>
                                                            {availableMetadataSortKeys.map((sortKey) => (
                                                                <option key={sortKey} value={sortKey}>
                                                                    {t(formatOverviewSortLabel(sortKey))}
                                                                </option>
                                                            ))}
                                                        </>
                                                    ) : null}
                                                </select>
                                                {(
                                                    !isSimplifiedSystemOverview ||
                                                    effectiveSelectedSortSensorType === SYSTEM_NAME_SORT_KEY
                                                ) && effectiveSelectedSortSensorType ? (
                                                    <div className="join">
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm join-item ${effectiveSelectedSortDirection === 'asc' ? 'border-base-content/15 bg-base-content text-base-100 hover:border-base-content/15 hover:bg-base-content/90' : 'btn-ghost'}`}
                                                            onClick={() => setSelectedSortDirection('asc')}
                                                            aria-label={t('Ascending')}
                                                            title={t('Ascending')}
                                                        >
                                                            <ArrowUp className="h-4 w-4" aria-hidden="true" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm join-item ${effectiveSelectedSortDirection === 'desc' ? 'border-base-content/15 bg-base-content text-base-100 hover:border-base-content/15 hover:bg-base-content/90' : 'btn-ghost'}`}
                                                            onClick={() => setSelectedSortDirection('desc')}
                                                            aria-label={t('Descending')}
                                                            title={t('Descending')}
                                                        >
                                                            <ArrowDown className="h-4 w-4" aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="flex flex-1 justify-center">
                                                <div className="inline-flex flex-wrap items-center gap-3">
                                                    {!isAnomalyFilterHidden ? (
                                                        <>
                                                            <label
                                                                className="text-sm font-semibold"
                                                                htmlFor={`${idPrefix}-anomaly-filter-select`}
                                                            >
                                                                {t('Anomaly')}
                                                            </label>
                                                            <select
                                                                id={`${idPrefix}-anomaly-filter-select`}
                                                                className="select select-bordered w-auto"
                                                                value={effectiveSelectedAnomaly}
                                                                onChange={(event) => {
                                                                    setSelectedAnomaly(event.target.value);
                                                                }}
                                                            >
                                                                <option value="">{t('All')}</option>
                                                                {anomalyFilterOptions.map(({ label, value }) => {
                                                                    return (
                                                                        <option key={value} value={value}>
                                                                            {t(label)}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </>
                                                    ) : null}

                                                    {!isStatusFilterHidden ? (
                                                        <>
                                                            <label
                                                                className="text-sm font-semibold"
                                                                htmlFor={`${idPrefix}-status-filter-select`}
                                                            >
                                                                {t('Status')}
                                                            </label>
                                                            <select
                                                                id={`${idPrefix}-status-filter-select`}
                                                                className="select select-bordered w-auto"
                                                                value={effectiveSelectedStatus}
                                                                onChange={(event) => {
                                                                    setSelectedStatus(event.target.value as OverviewStatusCategory | '');
                                                                }}
                                                            >
                                                                <option value="">{t('All')}</option>
                                                                <option value="danger">{t('Error')}</option>
                                                                <option value="warning">{t('Warning')}</option>
                                                                <option value="success">{t('OK')}</option>
                                                            </select>
                                                        </>
                                                    ) : null}

                                                    {!isSystemFilterHidden ? (
                                                        <>
                                                            <label
                                                                className="text-sm font-semibold"
                                                                htmlFor={`${idPrefix}-system-filter-select`}
                                                            >
                                                                {t('System')}
                                                            </label>
                                                            <select
                                                                id={`${idPrefix}-system-filter-select`}
                                                                className="select select-bordered w-auto"
                                                                value={effectiveSelectedSystem}
                                                                onChange={(event) => {
                                                                    setSelectedSystem(event.target.value);
                                                                }}
                                                            >
                                                                <option value="">{t('All')}</option>
                                                                {systemOptions.map((system) => (
                                                                    <option key={system} value={system}>
                                                                        {system}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </>
                                                    ) : null}

                                                    {!isCustomerFilterHidden ? (
                                                        <>
                                                            <label
                                                                className="text-sm font-semibold"
                                                                htmlFor={`${idPrefix}-customer-filter-select`}
                                                            >
                                                                {t('Customer')}
                                                            </label>
                                                            <select
                                                                id={`${idPrefix}-customer-filter-select`}
                                                                className="select select-bordered w-auto"
                                                                value={effectiveSelectedCustomer}
                                                                onChange={(event) => {
                                                                    setSelectedCustomer(event.target.value);
                                                                }}
                                                            >
                                                                <option value="">{t('All')}</option>
                                                                {customerOptions.map((customer) => (
                                                                    <option key={customer} value={customer}>
                                                                        {customer}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <div className="inline-flex min-w-fit items-center justify-end gap-2">
                                                <div className="text-right text-sm font-medium text-base-content/70">
                                                    {displayedCardCount} {displayedCardTypeLabel}
                                                </div>
                                                {shouldShowResetSelectionButton ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm gap-2"
                                                        onClick={() => {
                                                            setSelectedAnomaly('');
                                                            setSelectedCustomer('');
                                                            setSelectedSortDirection('desc');
                                                            setSelectedSortSensorType('status');
                                                            setSelectedStatus('');
                                                            setSelectedSystem('');
                                                        }}
                                                        title={t('Reset selection and sorting')}
                                                    >
                                                        <RotateCcw className="h-4 w-4" aria-hidden="true" />
                                                        <span>{t('Reset')}</span>
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {displayedCardCount === 0 ? (
                                    <div className="dashboard-panel col-span-full !min-h-44 items-start justify-start overflow-hidden p-5 text-left text-base-content/80">
                                        {t('No sensors were found for the current selection.')}
                                    </div>
                                ) : shouldShowSystemStatusGroups ? (
                                    systemStatusGroups.flatMap(({ cards: statusGroupCards, statusCategory }) => {
                                        const statusLabel = statusCategory === 'danger'
                                            ? t('Error')
                                            : statusCategory === 'warning'
                                                ? t('Warning')
                                                : t('OK');
                                        const statusBadgeClassName = statusCategory === 'danger'
                                            ? 'badge-error'
                                            : statusCategory === 'warning'
                                                ? 'badge-warning'
                                                : 'badge-success';
                                        const statusLineClassName = statusCategory === 'danger'
                                            ? 'bg-error'
                                            : statusCategory === 'warning'
                                                ? 'bg-warning'
                                                : 'bg-success';

                                        return [
                                            <h3
                                                key={`${statusCategory}-heading`}
                                                className="col-span-full flex items-end gap-0 pt-4 first:pt-0"
                                            >
                                                <span className={`badge ${statusBadgeClassName}`}>
                                                    {statusLabel}
                                                </span>
                                                <span className={`h-px flex-1 ${statusLineClassName}`} aria-hidden="true" />
                                            </h3>,
                                            ...statusGroupCards.map((card) => (
                                                <OverviewSystemCard
                                                    key={card.system}
                                                    card={card}
                                                    showKpis={showKpis}
                                                />
                                            )),
                                        ];
                                    })
                                ) : shouldShowSystemNameGroups ? (
                                    systemNameGroups.flatMap(({ cards: nameGroupCards, initial }) => [
                                        <h3
                                            key={`${initial}-heading`}
                                            className="col-span-full flex items-end gap-0 pt-4 first:pt-0"
                                        >
                                            <span className="badge border-base-content/15 bg-base-content text-base-100">
                                                {initial}
                                            </span>
                                            <span className="h-px flex-1 bg-base-content" aria-hidden="true" />
                                        </h3>,
                                        ...nameGroupCards.map((card) => (
                                            <OverviewSystemCard
                                                key={card.system}
                                                card={card}
                                                showKpis={showKpis}
                                            />
                                        )),
                                    ])
                                ) : groupBy === 'anomaly'
                                    ? sortedAnomalyCards.map((card) => (
                                        <OverviewAnomalyCard
                                            key={card.anomalyConfig.key}
                                            card={card}
                                            showKpis={showKpis}
                                        />
                                    ))
                                    : groupBy === 'system'
                                        ? sortedSystemCards.map((card) => (
                                        <OverviewSystemCard
                                            key={card.system}
                                            card={card}
                                            showKpis={showKpis}
                                        />
                                        ))
                                        : groupBy === 'status'
                                            ? sortedStatusCards.map((card) => (
                                                <OverviewStatusCard
                                                    key={card.statusCategory}
                                                    card={card}
                                                    showKpis={showKpis}
                                                />
                                            ))
                                            : groupBy === 'customer'
                                                ? sortedCustomerCards.map((card) => (
                                                    <OverviewCustomerCard
                                                        key={card.customer}
                                                        card={card}
                                                        showKpis={showKpis}
                                                    />
                                                ))
                                        : sortedCards.map((card) => (
                                        <OverviewSensorCard
                                            key={`${card.anomalyConfig.key}-${card.deviceName}`}
                                            card={card}
                                            showKpis={showKpis}
                                        />
                                        ))}
                            </>
                        );
                    }}
                </Api2LastValueData>
            </section>
        </div>
    );
}

export default AnomalyMetricsOverview;
